/**
 * NavBar — Left-side navigation for the Chanelle OS Custom Object App.
 *
 * Renders one nav item per APP_TAB. Active tab is highlighted.
 * Badges show unread counts on Inbox and overdue count on My Work.
 */

import { useCallback } from 'react';
import { Tooltip } from 'monday-ui-react-core';
import { useAppStore } from '../../core/store/appStore';
import { APP_TABS } from '../../core/utils/defaults';
import type { AppTab } from '../../core/types';

interface NavBadgeProps {
  count: number;
}

function NavBadge({ count }: NavBadgeProps) {
  if (!count) return null;
  return (
    <span className="nav-badge" aria-label={`${count} items`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface NavItemProps {
  tab: AppTab;
  isActive: boolean;
  badge?: number;
  onClick: (tab: AppTab) => void;
}

function NavItem({ tab, isActive, badge = 0, onClick }: NavItemProps) {
  return (
    <Tooltip content={tab.label} position={Tooltip.positions.RIGHT}>
      <button
        className={`nav-tab-btn ${isActive ? 'nav-tab-btn--active' : ''}`}
        onClick={() => onClick(tab)}
        aria-label={tab.label}
        aria-current={isActive ? 'page' : undefined}
      >
        <span aria-hidden="true">{tab.icon}</span>
        <span className="nav-tab-btn__label">{tab.label}</span>
        <NavBadge count={badge} />
      </button>
    </Tooltip>
  );
}

interface NavBarProps {
  inboxUnread?: number;
  overdueCount?: number;
}

export function NavBar({ inboxUnread = 0, overdueCount = 0 }: NavBarProps) {
  const { activeTab, setActiveTab } = useAppStore();

  const handleClick = useCallback(
    (tab: AppTab) => {
      setActiveTab(tab.id);
    },
    [setActiveTab]
  );

  const badgeFor = (tabId: string): number => {
    if (tabId === 'inbox')   return inboxUnread;
    if (tabId === 'my_work') return overdueCount;
    return 0;
  };

  return (
    <nav className="nav-rail" aria-label="Main navigation">
      <div className="nav-rail__brand" aria-hidden="true">
        <span className="nav-rail__brand-icon">✦</span>
      </div>
      <ul className="nav-rail__items" role="list">
        {APP_TABS.map((tab) => (
          <li key={tab.id} role="listitem">
            <NavItem
              tab={tab}
              isActive={activeTab === tab.id}
              badge={badgeFor(tab.id)}
              onClick={handleClick}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}
